import { Component } from '@theme/component';
import { debounce, onDocumentLoaded } from '@theme/utilities';
import { MegaMenuHoverEvent } from '@theme/events';

const ACTIVATE_DELAY = 0;
const DEACTIVATE_DELAY = 150;

/**
 * A custom element that manages a header menu.
 *
 * @typedef {Object} State
 * @property {HTMLElement | null} activeItem - The currently active menu item.
 *
 * @typedef {object} Refs
 * @property {HTMLElement} overflowMenu - The overflow menu.
 * @property {HTMLElement[]} [submenu] - The submenu in each respective menu item.
 *
 * @extends {Component<Refs>}
 */
class HeaderMenu extends Component {
  requiredRefs = ['overflowMenu'];

  #abortController = new AbortController();

  connectedCallback() {
    super.connectedCallback();

    // Ensure menu starts in closed state
    this.ariaExpanded = 'false';
    this.style.setProperty('--submenu-height', '0px');
    this.style.setProperty('--submenu-opacity', '0');

    // Force close all submenus on page load
    this.#forceCloseAllSubmenusOnLoad();

    this.overflowMenu?.addEventListener('pointerleave', () => this.#debouncedDeactivate(), {
      signal: this.#abortController.signal,
    });

    // Add click event listener to force submenu deactivation when clicking on menu links
    this.addEventListener('click', this.#handleMenuClick, {
      signal: this.#abortController.signal,
    });

    // Listen for navigation events to clean up menu state
    this.#setupNavigationListeners();

    onDocumentLoaded(this.#preloadImages);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#abortController.abort();
  }

  /**
   * @type {State}
   */
  #state = {
    activeItem: null,
  };

  /**
   * Time to allow for a closing animation between initiating a deactivation and actually deactivating the active item.
   * @returns {number}
   */
  get animationDelay() {
    const value = this.dataset.animationDelay;
    return value ? parseInt(value, 10) : 0;
  }

  /**
   * Get the overflow menu
   */
  get overflowMenu() {
    return /** @type {HTMLElement | null} */ (this.refs.overflowMenu?.shadowRoot?.querySelector('[part="overflow"]'));
  }

  /**
   * Whether the overflow menu is hovered
   * @returns {boolean}
   */
  get overflowHovered() {
    return this.refs.overflowMenu?.matches(':hover') ?? false;
  }

  /**
   * Activate the selected menu item immediately
   * @param {PointerEvent | FocusEvent} event
   */
  activate = (event) => {
    this.#debouncedDeactivate.cancel();
    this.#debouncedActivateHandler.cancel();

    this.#debouncedActivateHandler(event);
  };

  /**
   * Activate the selected menu item with a delay
   * @param {PointerEvent | FocusEvent} event
   */
  #activateHandler = (event) => {
    this.#debouncedDeactivate.cancel();

    this.dispatchEvent(new MegaMenuHoverEvent());

    this.removeAttribute('data-animating');

    if (!(event.target instanceof Element)) return;

    let item = findMenuItem(event.target);

    if (!item || item == this.#state.activeItem) return;

    // Check if this submenu was recently manually closed
    let submenu = findSubmenu(item);
    if (submenu && submenu.hasAttribute('data-manually-closed')) {
      // Remove the flag when hovering to allow reopening
      submenu.removeAttribute('data-manually-closed');
    }

    // Mark submenu as active for hover
    if (submenu) {
      submenu.classList.add('menu-list__submenu--active');
    }

    // Additional check: only activate on actual hover events, not on page load or navigation
    if (event.type === 'focus' && !event.target.matches(':hover')) {
      // Don't activate on focus events unless there's actual hover
      return;
    }

    const isDefaultSlot = event.target.slot === '';

    this.dataset.overflowExpanded = (!isDefaultSlot).toString();

    const previouslyActiveItem = this.#state.activeItem;

    if (previouslyActiveItem) {
      previouslyActiveItem.ariaExpanded = 'false';
    }

    this.#state.activeItem = item;
    this.ariaExpanded = 'true';
    item.ariaExpanded = 'true';

    let overflowMenuHeight = this.overflowMenu?.offsetHeight ?? 0;

    if (!submenu && !isDefaultSlot) {
      submenu = this.overflowMenu;
    }

    // Restore submenu properties when opening
    if (submenu) {
      // Remove the manually closed flag
      submenu.removeAttribute('data-manually-closed');
      
      // Restore all properties
      submenu.style.display = '';
      submenu.style.height = '';
      submenu.style.opacity = '';
      submenu.style.visibility = '';
      submenu.style.overflow = '';
      
      // Force a reflow to ensure proper rendering
      submenu.offsetHeight;
    }

    const submenuHeight = submenu ? Math.max(submenu.offsetHeight, overflowMenuHeight) : 0;

    this.style.setProperty('--submenu-height', `${submenuHeight}px`);
    this.style.setProperty('--submenu-opacity', '1');
  };

  #debouncedActivateHandler = debounce(this.#activateHandler, ACTIVATE_DELAY);

  /**
   * Deactivate the active item after a delay
   * @param {PointerEvent | FocusEvent} event
   */
  deactivate(event) {
    this.#debouncedActivateHandler.cancel();

    if (!(event.target instanceof Element)) return;

    const item = findMenuItem(event.target);

    // Make sure the item to be deactivated is still the active one. Ideally
    // we cancelled the debounce before the item was changed, but just in case.
    if (item === this.#state.activeItem) {
      this.#debouncedDeactivate();
    }
  }

  /**
   * Deactivate the active item immediately
   * @param {HTMLElement | null} [item]
   */
  #deactivate = (item = this.#state.activeItem) => {
    if (!item || item != this.#state.activeItem) return;
    
    // Always allow deactivation when clicking on links
    // Only prevent deactivation when hovering over overflow menu and not navigating
    if (this.overflowHovered && !this.#clickedLink) return;

    // Set CSS properties on the header menu
    this.style.setProperty('--submenu-height', '0px');
    this.style.setProperty('--submenu-opacity', '0');
    this.dataset.overflowExpanded = 'false';

    // Also directly manipulate the submenu element to ensure it closes
    const submenu = findSubmenu(item);
    if (submenu) {
      submenu.style.height = '0px';
      submenu.style.opacity = '0';
      submenu.style.visibility = 'hidden';
      submenu.style.overflow = 'hidden';
      // Remove active class and add flag
      submenu.classList.remove('menu-list__submenu--active');
      submenu.setAttribute('data-manually-closed', 'true');
    }

    // Clean up all submenus to prevent any from staying open
    this.#cleanupAllSubmenus();

    // Reset state
    this.#state.activeItem = null;
    this.ariaExpanded = 'false';
    item.ariaExpanded = 'false';
    item.setAttribute('data-animating', '');

    setTimeout(() => {
      item.removeAttribute('data-animating');
      // Ensure submenu is completely closed after animation
      if (submenu) {
        submenu.style.display = 'none';
        submenu.style.visibility = 'hidden';
        submenu.style.opacity = '0';
        submenu.style.height = '0px';
        submenu.style.overflow = 'hidden';
      }
    }, Math.max(0, this.animationDelay - 150)); // Start header transition 150ms before submenu finishes

    // Keep the manually closed flag until next hover - no automatic reopening
  };

  /**
   * Track if a link was recently clicked
   */
  #clickedLink = false;

  /**
   * Deactivate the active item after a delay
   * @param {PointerEvent | FocusEvent} event
   */
  #debouncedDeactivate = debounce(this.#deactivate, DEACTIVATE_DELAY);

  /**
   * Handle clicks on menu links to force immediate submenu deactivation
   * @param {MouseEvent} event
   */
  #handleMenuClick = (event) => {
    // Handle clicks on any menu link (main menu or submenu)
    if (event.target instanceof Element) {
      const clickedLink = event.target.closest('a[href]');
      if (clickedLink) {
        // Check if this link navigates to a different page
        const currentUrl = window.location.href.split('?')[0];
        const linkUrl = clickedLink instanceof HTMLAnchorElement ? clickedLink.href.split('?')[0] : '';
        
        if (linkUrl && linkUrl !== currentUrl) {
          // This link navigates to a different page - force close all menus immediately
          this.#forceCloseAllMenus();
        } else {
          // Same page link - just mark as clicked
          this.#clickedLink = true;
          
          // Force immediate deactivation without delay
          this.#deactivate();
          
          // Reset the flag after a short delay to allow normal hover behavior to resume
          setTimeout(() => {
            this.#clickedLink = false;
          }, 100);
        }
      }
    }
  };

  /**
   * Handle clicks outside the menu to close it.
   * @param {MouseEvent} event
   */
  #handleGlobalClick = (event) => {
    // Check if the click is outside the menu and its submenus
    if (this.overflowMenu && event.target instanceof Node && !this.overflowMenu.contains(event.target)) {
      this.#debouncedDeactivate();
    }
  };

  /**
   * Simple solution: listen for clicks anywhere on the page to close menu
   */
  #setupNavigationListeners = () => {
    document.addEventListener('click', this.#handleGlobalClick, {
      signal: this.#abortController.signal,
    });
  };


  /**
   * Preload images that are set to load lazily.
   */
  #preloadImages = () => {
    const images = this.querySelectorAll('img[loading="lazy"]');
    images?.forEach((image) => image.removeAttribute('loading'));
  };

  /**
   * Clean up all submenus to prevent any from staying open.
   */
  #cleanupAllSubmenus = () => {
    const submenus = this.querySelectorAll('[ref="submenu[]"]');
    submenus.forEach(submenu => {
      if (submenu instanceof HTMLElement) {
        submenu.style.height = '0px';
        submenu.style.opacity = '0';
        submenu.style.visibility = 'hidden';
        submenu.style.overflow = 'hidden';
        submenu.setAttribute('data-manually-closed', 'true'); // Mark as manually closed
      }
    });
  };

  /**
   * Force close all menus immediately.
   */
  #forceCloseAllMenus = () => {
    this.#debouncedDeactivate.cancel();
    this.#clickedLink = true;
    this.#deactivate();
    setTimeout(() => {
      this.#clickedLink = false;
    }, 100);
  };

  /**
   * Force close all submenus on page load to prevent white bars from appearing.
   */
  #forceCloseAllSubmenusOnLoad = () => {
    // Find all submenus and force them closed
    const submenus = this.querySelectorAll('[ref="submenu[]"]');
    submenus.forEach(submenu => {
      if (submenu instanceof HTMLElement) {
        submenu.style.display = 'none';
        submenu.style.visibility = 'hidden';
        submenu.style.opacity = '0';
        submenu.style.height = '0px';
        submenu.style.overflow = 'hidden';
        submenu.classList.remove('menu-list__submenu--active');
        submenu.setAttribute('data-manually-closed', 'true');
      }
    });
  };
}

if (!customElements.get('header-menu')) {
  customElements.define('header-menu', HeaderMenu);
}

/**
 * Find the closest menu item.
 * @param {Element | null | undefined} element
 * @returns {HTMLElement | null}
 */
function findMenuItem(element) {
  if (!(element instanceof Element)) return null;

  if (element?.matches('[slot="more"')) {
    // Select the first overflowing menu item when hovering over the "More" item
    return findMenuItem(element.parentElement?.querySelector('[slot="overflow"]'));
  }

  return element?.querySelector('[ref="menuitem"]');
}

/**
 * Find the closest submenu.
 * @param {Element | null | undefined} element
 * @returns {HTMLElement | null}
 */
function findSubmenu(element) {
  const submenu = element?.parentElement?.querySelector('[ref="submenu[]"]');
  return submenu instanceof HTMLElement ? submenu : null;
}
